import React, { useState } from 'react';
import { Keyboard, Command, Zap, Search, Navigation, FileText, MessageCircle, Settings, HelpCircle } from 'lucide-react';
import SearchInput from '../../components/common/SearchInput';

const KeyboardShortcuts = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const shortcuts = [
    {
      category: 'Navigation',
      icon: <Navigation className="w-5 h-5" />,
      items: [
        { keys: ['F2'], description: 'Open global search (Master Admin)', macKeys: ['F2'] },
        { keys: ['Ctrl', 'K'], description: 'Open global search (legacy)', macKeys: ['⌘', 'K'] },
        { keys: ['Ctrl', '/'], description: 'Go to dashboard', macKeys: ['⌘', '/'] },
        { keys: ['Ctrl', 'B'], description: 'Toggle sidebar', macKeys: ['⌘', 'B'] },
        { keys: ['G', 'H'], description: 'Go to Home', macKeys: ['G', 'H'] },
        { keys: ['G', 'S'], description: 'Go to SMS', macKeys: ['G', 'S'] },
        { keys: ['G', 'R'], description: 'Go to Reports', macKeys: ['G', 'R'] },
      ]
    },
    {
      category: 'Actions',
      icon: <Zap className="w-5 h-5" />,
      items: [
        { keys: ['Ctrl', 'N'], description: 'Create new SMS', macKeys: ['⌘', 'N'] },
        { keys: ['Ctrl', 'T'], description: 'Create new template', macKeys: ['⌘', 'T'] },
        { keys: ['Ctrl', 'Shift', 'C'], description: 'Copy selected item', macKeys: ['⌘', '⇧', 'C'] },
        { keys: ['Ctrl', 'Enter'], description: 'Send SMS', macKeys: ['⌘', 'Enter'] },
        { keys: ['ESC'], description: 'Close modal or dropdown', macKeys: ['ESC'] },
      ]
    },
    {
      category: 'Templates',
      icon: <FileText className="w-5 h-5" />,
      items: [
        { keys: ['Ctrl', 'Shift', 'T'], description: 'Go to Templates', macKeys: ['⌘', '⇧', 'T'] },
        { keys: ['Ctrl', 'Shift', 'A'], description: 'Create new Template', macKeys: ['⌘', '⇧', 'A'] },
        { keys: ['Ctrl', 'E'], description: 'Edit selected Template', macKeys: ['⌘', 'E'] },
      ]
    },
    {
      category: 'Search & Filter',
      icon: <Search className="w-5 h-5" />,
      items: [
        { keys: ['/'], description: 'Quick search in current page', macKeys: ['/'] },
        { keys: ['Ctrl', 'F'], description: 'Find in page', macKeys: ['⌘', 'F'] },
        { keys: ['Ctrl', 'Shift', 'F'], description: 'Advanced search', macKeys: ['⌘', '⇧', 'F'] },
        { keys: ['↑', '↓'], description: 'Navigate search results', macKeys: ['↑', '↓'] },
        { keys: ['⏎'], description: 'Select search result', macKeys: ['⏎'] },
      ]
    },
    {
      category: 'Support',
      icon: <HelpCircle className="w-5 h-5" />,
      items: [
        { keys: ['Ctrl', '?'], description: 'Show keyboard shortcuts', macKeys: ['⌘', '?'] },
        { keys: ['Ctrl', 'Shift', 'H'], description: 'Open help center', macKeys: ['⌘', '⇧', 'H'] },
        { keys: ['Ctrl', 'Shift', 'S'], description: 'Open live support', macKeys: ['⌘', '⇧', 'S'] },
      ]
    },
    {
      category: 'System',
      icon: <Settings className="w-5 h-5" />,
      items: [
        { keys: ['Ctrl', ','], description: 'Open settings', macKeys: ['⌘', ','] },
        { keys: ['Ctrl', 'Shift', 'L'], description: 'Lock screen', macKeys: ['⌘', '⇧', 'L'] },
        { keys: ['Ctrl', 'Alt', 'D'], description: 'Toggle dark mode', macKeys: ['⌘', '⌥', 'D'] },
        { keys: ['F11'], description: 'Toggle fullscreen', macKeys: ['⌘', 'Ctrl', 'F'] },
      ]
    }
  ];

  const filteredShortcuts = shortcuts.map(category => ({
    ...category,
    items: category.items.filter(item => 
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.keys.some(key => key.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  })).filter(category => category.items.length > 0);

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Keyboard className="w-6 h-6" />
          Keyboard Shortcuts
        </h1>
        <p className="text-gray-600 mt-1">Work faster with these keyboard shortcuts</p>
      </div>

      <div className="mb-6">
        <SearchInput
          placeholder="Search shortcuts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '400px' }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredShortcuts.map((category, index) => (
          <div key={index} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                {category.icon}
                <h3 className="font-semibold text-gray-900">{category.category}</h3>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {category.items.map((item, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <span className="text-sm text-gray-700">{item.description}</span>
                  <div className="flex gap-1">
                    {(isMac ? item.macKeys : item.keys).map((key, keyIdx) => (
                      <React.Fragment key={keyIdx}>
                        <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-300 rounded">
                          {key}
                        </kbd>
                        {keyIdx < (isMac ? item.macKeys : item.keys).length - 1 && (
                          <span className="text-gray-400 mx-1">+</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-blue-50 p-6 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Command className="w-5 h-5" />
          Pro Tips
        </h3>
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Press <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-white border border-gray-300 rounded">F2</kbd> 
            {' '}anywhere in the app to quickly open global search (Master Admin only).
          </p>
          <p className="text-sm text-gray-600">
            Press <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-white border border-gray-300 rounded">Ctrl + ?</kbd> 
            {isMac && <span> or <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-white border border-gray-300 rounded">⌘ + ?</kbd></span>} 
            {' '}anywhere in the app to quickly bring up this shortcut guide.
          </p>
        </div>
        <div className="flex gap-3 mt-4">
          <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            Download PDF Guide
          </button>
          <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            Print Shortcuts
          </button>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcuts;
